/**
 * The "When to nudge" rows, as pure functions the editor and its tests share.
 *
 * A row is edited as text and only becomes a typed rule on save. Holding the raw input keeps a
 * half-typed number ("", "-") editable instead of snapping it to 0 or NaN under the cursor, and
 * puts every parsing rule in one place where it can be tested without rendering anything.
 */

import type { NudgeField, NudgeRule, NudgeRuleValue } from './types';

/** One editable row. `field` and `operator` are '' until chosen; `value` is the raw input. */
export interface NudgeRuleDraft {
	/** Local identity for React keys and drag and drop. Never sent to the server. */
	id: string;
	field: string;
	operator: string;
	value: string;
}

export type NudgeFieldMap = ReadonlyMap<string, NudgeField>;

export type ParseResult<T> = { ok: true; value: T } | { ok: false; error: string };

let rowSequence = 0;

const nextRowId = (): string => {
	rowSequence += 1;
	return `nudge-rule-${rowSequence}`;
};

export const toFieldMap = (fields: NudgeField[]): NudgeFieldMap => new Map(fields.map((field) => [field.key, field]));

/** A row for a field (or an empty one), with the field's first operator already chosen. */
export const createRuleDraft = (field?: NudgeField): NudgeRuleDraft => ({
	id: nextRowId(),
	field: field?.key ?? '',
	operator: field?.operators[0]?.value ?? '',
	value: ''
});

/** A saved rule back into an editable row. */
export const ruleToDraft = (rule: NudgeRule): NudgeRuleDraft => ({
	id: nextRowId(),
	field: rule.field,
	operator: rule.operator,
	value: rule.value === null || rule.value === undefined ? '' : String(rule.value)
});

/**
 * A new field means a new operator list and a new kind of value, so both reset: "more than 5"
 * carried from Cart idle onto Current page URL would read as a URL of "5".
 */
export const changeRuleField = (row: NudgeRuleDraft, field: NudgeField): NudgeRuleDraft => {
	if (row.field === field.key) return row;
	return { ...row, field: field.key, operator: field.operators[0]?.value ?? '', value: '' };
};

/** The fields some row already uses. A field is used at most once per nudge. */
export const takenFieldKeys = (rows: NudgeRuleDraft[]): ReadonlySet<string> =>
	new Set(rows.map((row) => row.field).filter(Boolean));

/**
 * Whether a field can be picked for a row whose field is `rowField` (or, with none, added as a
 * new row). The row's own field stays selectable so its menu can show the check mark.
 */
export const isFieldSelectable = (field: NudgeField, taken: ReadonlySet<string>, rowField?: string): boolean =>
	field.key === rowField || !taken.has(field.key);

/** Moves one row, for drag and drop and the handle's arrow keys. Out-of-range moves are a no-op. */
export const moveItem = <T>(items: T[], from: number, to: number): T[] => {
	if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
	const next = [...items];
	const [moved] = next.splice(from, 1);
	next.splice(to, 0, moved as T);
	return next;
};

/** The suffix inside a number input. Percent is drawn as the sign, everything else as its word. */
export const unitSuffix = (unit: string | null): string | null => {
	if (!unit) return null;
	return unit === 'percent' ? '%' : unit;
};

/**
 * The operator as the closed select shows it. The catalog labels "≥ at least" so the open menu
 * reads well; the narrow trigger shows only the sign, as the design does.
 */
export const operatorTriggerLabel = (label: string): string => {
	const [first, ...rest] = label.split(' ');
	if (rest.length > 0 && first && !/[\p{L}\p{N}]/u.test(first)) return first;
	return label;
};

const parseNumberValue = (field: NudgeField, raw: string): ParseResult<number> => {
	const text = raw.trim();
	if (!text) return { ok: false, error: `Enter a value for ${field.label}.` };
	if (!/^-?\d+$/.test(text)) return { ok: false, error: `${field.label} must be a whole number.` };
	const value = Number(text);
	const suffix = unitSuffix(field.unit) ?? '';
	const unit = suffix && suffix !== '%' ? ` ${suffix}` : suffix;
	if (field.min !== null && value < field.min) {
		return { ok: false, error: `${field.label} must be at least ${field.min}${unit}.` };
	}
	if (field.max !== null && value > field.max) {
		return { ok: false, error: `${field.label} must be at most ${field.max}${unit}.` };
	}
	return { ok: true, value };
};

const parseStringValue = (field: NudgeField, raw: string): ParseResult<string> => {
	const value = raw.trim();
	if (!value) return { ok: false, error: `Enter a value for ${field.label}.` };
	if (field.max !== null && value.length > field.max) {
		return { ok: false, error: `${field.label} can be at most ${field.max} characters.` };
	}
	return { ok: true, value };
};

/** Booleans and other option values travel as the option's own value, matched by its text form. */
const parseOptionValue = (field: NudgeField, raw: string): ParseResult<NudgeRuleValue> => {
	const option = (field.options ?? []).find((candidate) => String(candidate.value) === raw);
	if (option) return { ok: true, value: option.value };
	if (field.valueType === 'boolean' && (raw === 'true' || raw === 'false')) return { ok: true, value: raw === 'true' };
	return { ok: false, error: `Choose a value for ${field.label}.` };
};

/** The raw input as the value the server expects for this field. */
export const parseRuleValue = (field: NudgeField, raw: string): ParseResult<NudgeRuleValue> => {
	switch (field.valueType) {
		case 'none':
			return { ok: true, value: null };
		case 'number':
			return parseNumberValue(field, raw);
		case 'string':
			return parseStringValue(field, raw);
		case 'boolean':
			return parseOptionValue(field, raw);
	}
};

export const MISSING_CONDITION_MESSAGE = 'Add at least one condition.';

/**
 * The rows as the rules the server stores, or the first thing wrong with them.
 *
 * A row with no field chosen is dropped rather than refused: it is the blank row a new nudge
 * opens with, and saving should not fail over a row nobody filled in.
 */
export const draftsToRules = (rows: NudgeRuleDraft[], fieldMap: NudgeFieldMap): ParseResult<NudgeRule[]> => {
	const filled = rows.filter((row) => row.field);
	if (filled.length === 0) return { ok: false, error: MISSING_CONDITION_MESSAGE };

	const seen = new Set<string>();
	const rules: NudgeRule[] = [];
	for (const row of filled) {
		const field = fieldMap.get(row.field);
		if (!field) return { ok: false, error: `"${row.field}" is no longer available. Remove that row.` };
		if (seen.has(field.key)) return { ok: false, error: `${field.label} is used more than once.` };
		seen.add(field.key);
		if (!field.operators.some((operator) => operator.value === row.operator)) {
			return { ok: false, error: `Choose an operator for ${field.label}.` };
		}
		const parsed = parseRuleValue(field, row.value);
		if (!parsed.ok) return parsed;
		rules.push({ field: field.key, operator: row.operator, value: parsed.value });
	}
	return { ok: true, value: rules };
};
